/**
 * Compliance Repository
 * COMPLETE implementation for compliance management
 */

import { BaseRepository } from '../BaseRepository';
import { db } from '../db';
import type { Audit, NonConformity, ComplianceCheck } from '../../../types/database';

export class AuditRepository extends BaseRepository<Audit> {
  constructor() {
    super(db.audits, 'audits');
  }

  async findByType(auditType: Audit['auditType']): Promise<Audit[]> {
    return await this.table.where('auditType').equals(auditType).toArray();
  }

  async findByStatus(status: Audit['status']): Promise<Audit[]> {
    return await this.table.where('status').equals(status).toArray();
  }

  async findUpcoming(days: number = 30): Promise<Audit[]> {
    const now = new Date();
    const futureDate = new Date();
    futureDate.setDate(futureDate.getDate() + days);

    return await this.table
      .where('scheduledDate')
      .between(now, futureDate, true, true)
      .filter(audit => audit.status === 'scheduled')
      .toArray();
  }

  async findOverdue(): Promise<Audit[]> {
    const now = new Date();
    return await this.table
      .where('status')
      .equals('scheduled')
      .filter(audit => new Date(audit.scheduledDate) < now)
      .toArray();
  }

  async findByDateRange(startDate: Date, endDate: Date): Promise<Audit[]> {
    return await this.table
      .where('scheduledDate')
      .between(startDate, endDate, true, true)
      .toArray();
  }
}

export class NonConformityRepository extends BaseRepository<NonConformity> {
  constructor() {
    super(db.nonConformities, 'nonConformities');
  }

  async findBySeverity(severity: NonConformity['severity']): Promise<NonConformity[]> {
    return await this.table.where('severity').equals(severity).toArray();
  }

  async findByStatus(status: NonConformity['status']): Promise<NonConformity[]> {
    return await this.table.where('status').equals(status).toArray();
  }

  async getOpen(): Promise<NonConformity[]> {
    return await this.table
      .where('status')
      .anyOf(['open', 'in_progress'])
      .toArray();
  }

  async getCritical(): Promise<NonConformity[]> {
    return await this.table
      .where('severity')
      .anyOf(['major', 'critical'])
      .toArray();
  }

  async findOverdue(): Promise<NonConformity[]> {
    const now = new Date();
    return await this.table
      .where('status')
      .anyOf(['open', 'in_progress'])
      .filter(nc => nc.targetCloseDate !== undefined && new Date(nc.targetCloseDate) < now)
      .toArray();
  }

  async findByDateRange(startDate: Date, endDate: Date): Promise<NonConformity[]> {
    return await this.table
      .where('identifiedDate')
      .between(startDate, endDate, true, true)
      .toArray();
  }
}

export class ComplianceCheckRepository extends BaseRepository<ComplianceCheck> {
  constructor() {
    super(db.complianceChecks, 'complianceChecks');
  }

  async findByType(checkType: string): Promise<ComplianceCheck[]> {
    return await this.table.where('checkType').equals(checkType).toArray();
  }

  async findNonCompliant(): Promise<ComplianceCheck[]> {
    return await this.table.filter(check => !check.compliant).toArray();
  }

  async findByDateRange(startDate: Date, endDate: Date): Promise<ComplianceCheck[]> {
    return await this.table
      .where('checkDate')
      .between(startDate, endDate, true, true)
      .toArray();
  }

  async getComplianceRate(checkType?: string): Promise<number> {
    let checks: ComplianceCheck[];
    
    if (checkType) {
      checks = await this.findByType(checkType);
    } else {
      checks = await this.table.toArray();
    }

    if (checks.length === 0) return 100;

    const compliantCount = checks.filter(c => c.compliant).length;
    return (compliantCount / checks.length) * 100;
  }
}

export const auditRepository = new AuditRepository();
export const nonConformityRepository = new NonConformityRepository();
export const complianceCheckRepository = new ComplianceCheckRepository();


